import type Highcharts from "highcharts";
import React from "react";
import type { ActiveAnalysis } from "../../../../../../api/useActiveAnalysesStream";
import type { Project } from "../../../../../../api/useProjects";
import type { Setup } from "../../../../../../api/useSetups";
import { internalGapData } from "../../../../Chart/series/getInternalGapSerie";
import {
  useMetrologyOverrides,
  type MetrologyOverrides,
} from "../../../../../../api/useMetrologyOverrides";
import { useTranslations, type Translation } from "../../../../../../api/useTranslations";
import type { Quantity } from "../../../../../../api/useQuantities";
import t from "../../../../../../i18n";
import seriesColors from "../../../../Chart/series/seriesColors";

const INTERNAL_GAP_WIDGET_ID = "INTERNAL_GAP_WIDGET_ID";
function getInternalGapSerie({
  analyses,
  setups,
  quantity,
  yAxisId,
  legendIndex,
  metrologyOverrides,
  translations,
}: {
  analyses: ActiveAnalysis[];
  setups: Setup[];
  quantity: Quantity;
  yAxisId: string;
  legendIndex: number;
  metrologyOverrides: MetrologyOverrides;
  translations: Translation;
}): Highcharts.SeriesAreaOptions {
  const data: Highcharts.PointOptionsObject[] = internalGapData({
    analyses,
    quantity,
    setups,
    metrologyOverrides,
  }).map(point => ({
    x: point.x,
    y: point.high,
    custom: point.custom,
  }));

  return {
    id: INTERNAL_GAP_WIDGET_ID,
    yAxis: yAxisId,
    name: t("charts.legend.internal_losses", translations),
    type: "area",
    replicateInNavigator: false,
    data,
    showInLegend: true,
    legendIndex,
    color: seriesColors.sparklineArea,
    lineColor: seriesColors.sparklineArea,
    fillColor: seriesColors.fadeSparklineArea,
    marker: {
      symbol: "circle",
      fillColor: seriesColors.sparklineMarker,
      lineColor: seriesColors.sparklineMarker,
      lineWidth: 2,
      radius: 2,
    },
  };
}

const useSeries = ({
  project,
  yAxisId,
  setups,
  analyses,
  performanceIndicator,
}: {
  project: Project;
  yAxisId: string;
  setups: Setup[];
  analyses: ActiveAnalysis[];
  performanceIndicator: Quantity | null;
}): Highcharts.SeriesAreaOptions[] => {
  const translations = useTranslations();
  const metrologyOverrides = useMetrologyOverrides(project.id);

  const areaRangeSeries = React.useMemo(() => {
    const series: Highcharts.SeriesAreaOptions[] = [];
    if (performanceIndicator) {
      series.push(
        getInternalGapSerie({
          yAxisId,
          analyses,
          setups,
          quantity: performanceIndicator,
          legendIndex: 0,
          metrologyOverrides,
          translations,
        }),
      );
    }
    return series;
  }, [analyses, metrologyOverrides, performanceIndicator, setups, translations, yAxisId]);

  return React.useMemo(() => areaRangeSeries.filter(s => s !== null), [areaRangeSeries]);
};

export default useSeries;
